import React, { useCallback, useState } from "react";
import { Button, DatePicker, Empty, Input, Modal, Select, Table, message } from "antd";
import {
  CheckOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { AUDIT_PLAN_CSV_COLUMNS, AUDIT_PLAN_STATUS_OPTIONS } from "../../constants/internalAuditWorkflow";
import { userMessageForError } from "../../utils/apiError";
import { formatDdMmYyyy } from "../../utils/internalAuditView";
import { iaRequest } from "./iaApi";

const MAX_NAME_LENGTH = 120;

function statusPill(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-ok-fg)]">
        <CheckOutlined /> Completed
      </span>
    );
  }
  if (s === "in_progress") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-accent)]">
        <ClockCircleOutlined /> In progress
      </span>
    );
  }
  if (s === "scheduled") {
    return (
      <span className="inline-flex rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Scheduled
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-text-muted)]">
      Not started
    </span>
  );
}

function rowsToCsv(rows) {
  const header = AUDIT_PLAN_CSV_COLUMNS.join(",");
  const lines = rows.map((r) =>
    [
      r.id,
      r.clause,
      r.auditArea,
      r.auditor,
      r.auditee,
      formatDdMmYyyy(r.scheduledDate),
      r.status,
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  return [header, ...lines].join("\n");
}

/**
 * Audit plan schedule. Lead Advisor / Advisor enter the dates, status and the named auditor / auditee /
 * lead auditor (nothing is pre-filled); circulating is a Lead Advisor act.
 */
export function AuditPlanSubTab({
  projectId,
  rows,
  planWorkflow,
  canEditPlan,
  /** Naming auditor / auditee / lead auditor is Lead Advisor / Advisor only; platform_admin sees them read-only. */
  canEditNames = false,
  /** Lead auditor stored on the package (a named person), or "". */
  leadAuditor = "",
  /** Circulating is a Lead Advisor act (API: 403 PLATFORM_ADMIN_APPROVAL_DENIED for platform_admin, PRD s2.2). */
  canCirculate = false,
  canViewPlan,
  getApiToken,
  onPackageUpdated,
}) {
  const [busy, setBusy] = useState(false);
  const [circulateOpen, setCirculateOpen] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  // Bumped when a save is refused so the uncontrolled name inputs remount showing the stored value.
  const [revision, setRevision] = useState(0);

  const patchRow = useCallback(
    async (rowId, patch) => {
      if (!projectId || !(canEditPlan || canEditNames)) return;
      setSavingRowId(rowId);
      setBusy(true);
      try {
        const data = await iaRequest(
          getApiToken,
          `/api/v1/projects/${projectId}/internal-audit/plan/${encodeURIComponent(rowId)}`,
          { method: "PATCH", body: patch },
        );
        if (data?.internalAuditPackage && onPackageUpdated) {
          onPackageUpdated(data.internalAuditPackage);
        }
      } catch (e) {
        message.error(userMessageForError(e, "Could not save row."));
        setRevision((n) => n + 1);
      } finally {
        setSavingRowId(null);
        setBusy(false);
      }
    },
    [projectId, canEditPlan, canEditNames, getApiToken, onPackageUpdated],
  );

  const saveLeadAuditor = useCallback(
    async (name) => {
      if (!projectId || !canEditNames) return;
      setBusy(true);
      try {
        const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/lead-auditor`, {
          method: "PATCH",
          body: { leadAuditor: name },
        });
        if (data?.internalAuditPackage && onPackageUpdated) {
          onPackageUpdated(data.internalAuditPackage);
        }
      } catch (e) {
        message.error(userMessageForError(e, "Could not save lead auditor."));
        setRevision((n) => n + 1);
      } finally {
        setBusy(false);
      }
    },
    [projectId, canEditNames, getApiToken, onPackageUpdated],
  );

  const handleCirculate = async () => {
    if (!projectId || !canCirculate) return;
    setBusy(true);
    try {
      const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/circulate-plan`, {
        method: "POST",
      });
      message.success("Audit plan circulated to all project roles.");
      setCirculateOpen(false);
      if (data?.internalAuditPackage && onPackageUpdated) {
        onPackageUpdated(data.internalAuditPackage);
      }
    } catch (e) {
      message.error(userMessageForError(e, "Could not circulate plan."));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "internal-audit-plan-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canViewPlan) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-12 shadow-sm">
        <Empty description="Audit plan not yet circulated by Lead Advisor." />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-12 shadow-sm">
        <Empty description="No audit plan has been set up for this project yet." />
      </div>
    );
  }

  const nameCell = (field, row, label) => {
    if (!canEditNames) return <span className="text-sm text-[color:var(--color-text-muted-dark)]">{row[field] || "-"}</span>;
    return (
      <Input
        key={`${row.id}-${field}-${row[field]}-${revision}`}
        size="small"
        aria-label={`${label} for ${row.id}`}
        defaultValue={row[field] || ""}
        maxLength={MAX_NAME_LENGTH}
        placeholder="Name"
        disabled={savingRowId === row.id || busy}
        onBlur={(e) => {
          const val = e.target.value.trim();
          if (val !== (row[field] || "")) void patchRow(row.id, { [field]: val });
        }}
      />
    );
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 72, render: (v) => <span className="font-mono text-xs font-bold text-[color:var(--color-text-muted-dark)]">{v}</span> },
    { title: "Clause", dataIndex: "clause", key: "clause", ellipsis: true },
    { title: "Audit Area", dataIndex: "auditArea", key: "auditArea", ellipsis: true },
    { title: "Auditor", key: "auditor", width: 160, render: (_, row) => nameCell("auditor", row, "Auditor") },
    { title: "Auditee", key: "auditee", width: 160, render: (_, row) => nameCell("auditee", row, "Auditee") },
    {
      title: "Scheduled Date",
      key: "scheduledDate",
      width: 160,
      render: (_, row) => {
        if (!canEditPlan) {
          return <span className="text-sm text-[color:var(--color-text-muted-dark)]">{formatDdMmYyyy(row.scheduledDate) || "Not scheduled"}</span>;
        }
        const d = row.scheduledDate ? dayjs(row.scheduledDate) : null;
        return (
          <DatePicker
            size="small"
            format="DD-MM-YYYY"
            placeholder="Not scheduled"
            allowClear={false}
            value={d?.isValid() ? d : null}
            disabled={savingRowId === row.id || busy}
            onChange={(val) => {
              if (!val) return;
              void patchRow(row.id, { scheduledDate: val.format("YYYY-MM-DD") });
            }}
            className="w-full"
          />
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 150,
      render: (_, row) => {
        if (!canEditPlan) return statusPill(row.status);
        return (
          <Select
            size="small"
            className="w-full min-w-[120px]"
            value={row.status}
            disabled={savingRowId === row.id || busy}
            options={AUDIT_PLAN_STATUS_OPTIONS}
            onChange={(v) => void patchRow(row.id, { status: v })}
          />
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {planWorkflow === "circulated" ? (
        <div className="rounded-xl border border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] px-4 py-3 text-sm font-semibold text-[color:var(--color-accent)]">
          Visible to all project roles. Lead Advisor can still update scheduled dates and status.
        </div>
      ) : null}

      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-lg font-black tracking-tight text-[color:var(--color-text-hero)]">Audit Plan Schedule</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              Download template
            </Button>
            {canCirculate && planWorkflow !== "circulated" ? (
              <Button type="primary" icon={<SendOutlined />} onClick={() => setCirculateOpen(true)}>
                Circulate audit plan
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label htmlFor="ia-lead-auditor" className="text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Lead auditor
          </label>
          {canEditNames ? (
            <Input
              key={`lead-${leadAuditor}-${revision}`}
              id="ia-lead-auditor"
              className="max-w-xs"
              size="small"
              defaultValue={leadAuditor}
              maxLength={MAX_NAME_LENGTH}
              placeholder="Named person"
              disabled={busy}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val !== leadAuditor) void saveLeadAuditor(val);
              }}
            />
          ) : (
            <span id="ia-lead-auditor" className="text-sm font-semibold text-[color:var(--color-text-hero)]">{leadAuditor || "-"}</span>
          )}
        </div>

        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: 1000 }}
          size="middle"
        />
      </div>

      <Modal
        title="Circulate audit plan?"
        open={circulateOpen}
        onCancel={() => setCirculateOpen(false)}
        onOk={() => void handleCirculate()}
        okText="Circulate"
        confirmLoading={busy}
      >
        <p className="text-sm text-[color:var(--color-text-muted)]">
          All project roles (Advisor, Company SPOC, Company Delegate) will be notified and can view the audit plan schedule.
        </p>
      </Modal>
    </div>
  );
}

export default AuditPlanSubTab;
